import React from 'react'

import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import { Text } from '@mxenabled/mxui'
import { CurrencyInput, formatCurrency, useGlobalCopyStore } from '../../../common'
import { buildDynamicCopy } from '../../../core/utils/Localization'
import { DetailedBudget } from '../../../core'
import { isInvalidAmount } from '../../../core/utils/BudgetUtil'

import { SubBudgetCategory } from '../shared/SubBudgetCategory'
import { Divider } from '@mui/material'

export interface SubBudgetAmountsProps {
  categoryName: string
  subBudgets: DetailedBudget[]
  subBudgetAmounts: Record<string, string>
  onSubBudgetAmountChanged: (guid: string, amount: string) => void
}

export const SubBudgetAmounts: React.FC<SubBudgetAmountsProps> = ({
  categoryName,
  subBudgets,
  subBudgetAmounts,
  onSubBudgetAmountChanged,
}) => {
  const { budgets: copy } = useGlobalCopyStore()

  const total = subBudgets.reduce((s, sb) => s + (Number(subBudgetAmounts[sb.guid]) || 0), 0)

  return (
    <React.Fragment>
      <Stack>
        <Text aria-live="polite" sx={{ pb: 12 }}>
          {
            buildDynamicCopy(
              copy.your_budget_summary,
              categoryName,
              <Text bold={true} component="span">
                {formatCurrency(total, '0,0')}
              </Text>,
            ) as React.ReactNode
          }
        </Text>
        <Stack sx={{ flexDirection: 'row', mt: 12 }}>
          <Text bold={true} id="sub-budget-col-name" sx={{ width: '50%' }} variant="caption">
            {copy.details_subbudgets_tab}
          </Text>
          <Text
            bold={true}
            id="sub-budget-col-total"
            sx={{ textAlign: 'right', width: '50%' }}
            variant="caption"
          >
            {copy.monthly_total}
          </Text>
        </Stack>
      </Stack>
      <List aria-label={copy.details_subbudgets_tab}>
        {subBudgets.map((subBudget, index) => {
          const val = subBudgetAmounts[subBudget.guid] ?? `${subBudget.amount}`
          return (
            <React.Fragment key={subBudget.guid}>
              <ListItem>
                <SubBudgetCategory budget={subBudget} />
                <CurrencyInput
                  amount={val}
                  aria-describedby="sub-budget-col-total"
                  aria-invalid={isInvalidAmount(val)}
                  label={subBudget.category.name}
                  minAmount={0}
                  setAmount={(newVal) => onSubBudgetAmountChanged(subBudget.guid, newVal)}
                  sx={{ '.MuiTypography-body1': { p: 0 }, width: '25%', flexShrink: 0 }}
                />
              </ListItem>
              <Divider sx={{ ml: index === subBudgets.length - 1 ? 0 : 48 }} />
            </React.Fragment>
          )
        })}
      </List>
    </React.Fragment>
  )
}
